import type { Map } from 'mapbox-gl';

type MiddleMousePanCursorHandler = (cursor: 'grabbing' | undefined) => void;

/**
 * Pan the design map with the middle mouse button (scroll-wheel click),
 * using a hand cursor while the button is held — same navigation feel as Space+drag.
 */
export class MiddleMousePanController {
  /** Defaults to Design's middle button; top-down previews can use RMB. */
  constructor(private readonly button: 1 | 2 = 1) {}

  private map: Map | undefined;
  private active = false;
  private lastClient: { x: number; y: number } | undefined;
  private onCursor: MiddleMousePanCursorHandler | undefined;
  private listenersAttached = false;

  /**
   * @param onCursor Optional hook so the map renderer can lock the hand cursor
   * while middle-pan is active (avoids hover cursors overwriting it).
   */
  setCursorHandler(handler: MiddleMousePanCursorHandler | undefined): void {
    this.onCursor = handler;
  }

  attach(map: Map): void {
    this.detach();
    this.map = map;
    this.attachListeners();
  }

  detach(): void {
    this.endPan();
    this.detachListeners();
    this.map = undefined;
  }

  /** True while the middle button is held and panning. */
  isActive(): boolean {
    return this.active;
  }

  private attachListeners(): void {
    if (this.listenersAttached || !this.map) return;
    const canvas = this.map.getCanvas();
    canvas.addEventListener('mousedown', this.handleMouseDown);
    // Stop browser autoscroll / default middle-click behavior on the canvas.
    canvas.addEventListener('auxclick', this.handleAuxClick);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    this.listenersAttached = true;
  }

  private detachListeners(): void {
    if (!this.listenersAttached) return;
    const canvas = this.map?.getCanvas();
    canvas?.removeEventListener('mousedown', this.handleMouseDown);
    canvas?.removeEventListener('auxclick', this.handleAuxClick);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    this.listenersAttached = false;
  }

  private readonly handleAuxClick = (event: MouseEvent): void => {
    if (event.button !== this.button) return;
    event.preventDefault();
  };

  private readonly handleMouseDown = (event: MouseEvent): void => {
    if (event.button !== this.button) return;
    event.preventDefault();
    this.map?.stop();
    this.active = true;
    this.lastClient = { x: event.clientX, y: event.clientY };
    this.onCursor?.('grabbing');
  };

  private readonly handleMouseMove = (event: MouseEvent): void => {
    if (!this.active || !this.map || !this.lastClient) return;
    const dx = event.clientX - this.lastClient.x;
    const dy = event.clientY - this.lastClient.y;
    this.lastClient = { x: event.clientX, y: event.clientY };
    // panBy expects screen delta of the map content; invert pointer delta.
    this.map.panBy([-dx, -dy], { animate: false });
  };

  private readonly handleMouseUp = (event: MouseEvent): void => {
    if (event.button !== this.button) return;
    this.endPan();
  };

  private endPan(): void {
    if (!this.active) return;
    this.active = false;
    this.lastClient = undefined;
    this.onCursor?.(undefined);
  }
}
